import React, { ReactElement } from "react"
import { Typography, Link, useTheme, Stack, Divider } from "@carrotfertility/carotene-core"
import { useFlags } from "launchdarkly-react-client-sdk"
import { useCurrentUser } from "../../context/user/UserContext"
import { useGetRoutingInfo } from "../shared/useQueryRoutingInfo"
import { useGetRegistrationJourney } from "../hooks/useGetRegistrationJourney"
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { useHistory } from "react-router-dom"
import { useShortJourneyLabel } from "../hooks/useJourneyOptions"
import useGetMemberBenefit from "../hooks/useGetMemberBenefit"
import { Benefit } from "../Benefit"

export function JourneyChange(): ReactElement {
  const { data: routingInfo, isLoading: isRoutingInfoLoading } = useGetRoutingInfo()
  const { registrationJourneyResult, isRegistrationJourneyLoading } = useGetRegistrationJourney()
  const { data: memberBenefit, isLoading: isMemberBenefitLoading } = useGetMemberBenefit()
  const { enrolledJourneySelectionHomePageDisplay, engagedJourneySelectionHomePageDisplay } = useFlags()
  const { isInAppLockdown } = useCurrentUser()
  const shouldShowEngagedJourneyDisplay = engagedJourneySelectionHomePageDisplay && !isInAppLockdown
  const shouldShowEnrolledJourneyDisplay = enrolledJourneySelectionHomePageDisplay && isInAppLockdown

  const allDataIsLoaded = !isMemberBenefitLoading && !isRoutingInfoLoading && !isRegistrationJourneyLoading

  if (shouldShowEnrolledJourneyDisplay && allDataIsLoaded) {
    let journeySelection: string = routingInfo?.journey
    journeySelection ??= registrationJourneyResult?.journey
    return <PreCarrotPlanJourneyChange journeySelection={journeySelection} memberBenefit={memberBenefit} />
  }

  if (shouldShowEngagedJourneyDisplay && allDataIsLoaded) {
    return <PostCarrotPlanJourneyChange />
  }

  return null
}

type PreCarrotPlanJourneyChangeProps = {
  journeySelection: string
  memberBenefit: Benefit
}

function PreCarrotPlanJourneyChange({
  journeySelection,
  memberBenefit
}: PreCarrotPlanJourneyChangeProps): ReactElement {
  const history = useHistory()
  const theme = useTheme()

  function sendToRouting() {
    history.push("/carrot-plans/about-you")
  }

  const { journeyLabel } = useShortJourneyLabel(memberBenefit, journeySelection)

  return (
    <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)}>
      {journeyLabel ? <JourneySelection journeyLabel={journeyLabel} /> : <ReadyToUnlockYourBenefit />}
      <Link component="button" onClick={sendToRouting}>
        {journeySelection ? (
          <FormattedMessage defaultMessage="Tell us about your journey to unlock your benefit" />
        ) : (
          <FormattedMessage defaultMessage="Tell us about your journey" />
        )}
      </Link>
    </Stack>
  )
}

type JourneySelectionProps = {
  journeyLabel: MessageDescriptor
}

function JourneySelection({ journeyLabel }: JourneySelectionProps): ReactElement {
  const theme = useTheme()
  const intl = useIntl()
  const formattedJourneyLabel = intl.formatMessage(journeyLabel)
  return (
    <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)}>
      <Typography>
        <FormattedMessage defaultMessage="Your journey: " />
      </Typography>
      <Typography>{formattedJourneyLabel}</Typography>
      <Divider orientation="vertical" />
    </Stack>
  )
}

function ReadyToUnlockYourBenefit(): ReactElement {
  return (
    <Typography>
      <FormattedMessage defaultMessage="Ready to unlock your benefit?" />
    </Typography>
  )
}

function PostCarrotPlanJourneyChange(): ReactElement {
  // eslint-disable-next-line formatjs/no-literal-string-in-jsx
  return <Typography>PostCarrotPlanJourneyChange</Typography>
}
